import _ from 'lodash';
import type { z } from 'zod';
import { LegacyExamAttemptStatus, LegacyExamAttemptType, type Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import { mergeMissedMarks } from '../legacyExamAttempts/utils';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId } from '../recordIds';
import { DBLegacyExamQuestionAttemptSchema, LegacyExamQuestionAttemptSchema } from './schemas';

type DBLegacyExamQuestionAttempt = z.infer<typeof DBLegacyExamQuestionAttemptSchema>;
type LegacyExamQuestionAttempt = z.infer<typeof LegacyExamQuestionAttemptSchema>;

export const LegacyExamQuestionAttemptCodecConfig: TransformerConfig<
  typeof LegacyExamQuestionAttemptSchema,
  typeof DBLegacyExamQuestionAttemptSchema,
  'id' | 'examId' | 'attemptId'
> = {
  name: 'LegacyExamQuestionAttempt',
  clientSchema: LegacyExamQuestionAttemptSchema,
  dbSchema: DBLegacyExamQuestionAttemptSchema,
  transformers: [
    createRenameTransformer<
      typeof LegacyExamQuestionAttemptSchema,
      typeof DBLegacyExamQuestionAttemptSchema
    >('studentId', 'student'),
    // subject, examCode, attemptNumber, questionId ↔ subjectExamQuestionIdentifier, exam, question
    createTransformer<
      typeof LegacyExamQuestionAttemptSchema,
      typeof DBLegacyExamQuestionAttemptSchema
    >({
      encoder: {
        sourceFields: ['subject', 'examCode', 'attemptNumber', 'questionId'] as const,
        encode: ({ subject, examCode, attemptNumber, questionId }) => ({
          exam: `${subject}#${examCode}`,
          subjectExamQuestionIdentifier: `${LegacyExamAttemptType.Question}#${subject}#${examCode}#${attemptNumber}#${questionId}`,
          question: questionId,
        }),
      },
      decoder: {
        sourceFields: ['subjectExamQuestionIdentifier', 'exam', 'question'] as const,
        decode: ({ subjectExamQuestionIdentifier }) => {
          if (subjectExamQuestionIdentifier === undefined) {
            return {};
          }
          const parts = subjectExamQuestionIdentifier.split('#');
          return {
            subject: parts[1] as Subject,
            examCode: parts[2] as string,
            attemptNumber: _.toInteger(parts[3]),
            questionId: parts[4] as string,
          };
        },
      },
    }),
    // questionLabelParts: ['1', 'a', 'iv'] ↔ questionLabel: '1#a#iv'
    createTransformer<
      typeof LegacyExamQuestionAttemptSchema,
      typeof DBLegacyExamQuestionAttemptSchema
    >({
      encoder: {
        sourceFields: ['questionLabelParts'] as const,
        encode: ({ questionLabelParts }) => ({
          questionLabel: (
            questionLabelParts as LegacyExamQuestionAttempt['questionLabelParts']
          ).join('#'),
        }),
      },
      decoder: {
        sourceFields: ['questionLabel'] as const,
        decode: ({ questionLabel }) => ({
          questionLabelParts: (questionLabel as DBLegacyExamQuestionAttempt['questionLabel']).split(
            '#'
          ) as LegacyExamQuestionAttempt['questionLabelParts'],
        }),
      },
    }),
    // Merge missedMarks into inaccuracyMarks on decode
    createTransformer<
      typeof LegacyExamQuestionAttemptSchema,
      typeof DBLegacyExamQuestionAttemptSchema
    >({
      decoder: {
        sourceFields: ['inaccuracyMarks', 'missedMarks'] as const,
        decode: data =>
          mergeMissedMarks(data as { inaccuracyMarks?: number; missedMarks?: number }),
      },
    }),
    // status: LegacyExamAttemptStatus ↔ status: 'QUESTION#status'
    createTransformer<
      typeof LegacyExamQuestionAttemptSchema,
      typeof DBLegacyExamQuestionAttemptSchema
    >({
      encoder: {
        sourceFields: ['status'] as const,
        encode: ({ status }) => ({
          status: `${LegacyExamAttemptType.Question}#${status}`,
        }),
      },
      decoder: {
        sourceFields: ['status'] as const,
        decode: ({ status }) => ({
          status: (status as DBLegacyExamQuestionAttempt['status']).split(
            '#'
          )[1] as LegacyExamAttemptStatus,
        }),
      },
    }),
  ],
  derived: {
    id: questionAttempt => buildRecordId('legacyExamQuestionAttempt', questionAttempt),
    examId: questionAttempt =>
      buildRecordId('legacyExam', {
        subject: questionAttempt.subject,
        code: questionAttempt.examCode,
      }),
    attemptId: questionAttempt => buildRecordId('legacyExamAttempt', questionAttempt),
  },
};

export const encodeLegacyExamQuestionAttemptToDb = createEncodeToDbFn(
  LegacyExamQuestionAttemptCodecConfig
);
export const decodeLegacyExamQuestionAttemptFromDb = createDecodeFromDbFn(
  LegacyExamQuestionAttemptCodecConfig
);
